import { postAction } from '../api/client';
import { currentSession } from '../auth/session';

const MAX_RAW_BYTES = 9 * 1024 * 1024; // mirrors MEDIA_MAX_BYTES in apps-script/Media.gs

/** Downscale + re-encode a photo so it uploads quickly on a mobile connection. */
async function compressImage(file: File, maxDim = 1600, quality = 0.72): Promise<Blob> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file; // decoding failed (rare/unsupported format) — upload the original
  }
  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.drawImage(bitmap, 0, 0, w, h);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  return blob ?? file;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result);
      resolve(s.slice(s.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Compress (photos) / size-check (video), then upload straight to the backend
 * (Drive-backed). Requires connectivity — media fields are not queued offline,
 * unlike the rest of a reading. Returns the file's URL, which becomes the
 * field's value.
 */
export async function captureAndUpload(
  file: File,
  kind: 'photo' | 'video',
  equipmentId: string,
  fieldKey: string,
): Promise<string> {
  const ses = currentSession();
  if (!ses) throw new Error('Not signed in.');

  let blob: Blob = file;
  let mimeType = file.type || (kind === 'photo' ? 'image/jpeg' : 'video/mp4');
  if (kind === 'photo' && file.type.startsWith('image/')) {
    blob = await compressImage(file);
    mimeType = 'image/jpeg';
  }
  if (blob.size > MAX_RAW_BYTES) {
    const mb = (blob.size / 1024 / 1024).toFixed(1);
    throw new Error(
      kind === 'video'
        ? `Video is ${mb}MB — too large to upload. Re-record a shorter clip.`
        : `Photo is ${mb}MB — too large to upload. Please try again.`,
    );
  }

  const data = await blobToBase64(blob);
  const res = await postAction<{ ok: boolean; url?: string; message?: string }>(
    'uploadMedia',
    { token: ses.token, data, mimeType, equipmentId, fieldKey },
    { timeoutMs: 90_000, retries: 0 },
  );
  if (!res.ok || !res.url) throw new Error(res.message || 'Upload failed.');
  return res.url;
}
